import React, { useState, useEffect, useCallback } from 'react';
import { dietTemplatesService } from '../../services/api';
import { ordenarRefeicoes } from './helpers';
import { Ico, emo } from '../ui/Ico';

const BRAND = '#D4A574';
const GREEN = '#46714f';

const MEAL_LABEL = {
  cafe_manha: 'Café da manhã', lanche_manha: 'Lanche da manhã', almoco: 'Almoço',
  lanche_tarde: 'Lanche da tarde', jantar: 'Jantar', ceia: 'Ceia',
};

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflowY: 'auto' };
const box = { background: '#ffffff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', margin: 'auto' };
const inputS = { width: '100%', padding: '0.6rem 0.85rem 0.6rem 2.2rem', border: '1.5px solid #ebe3da', borderRadius: '10px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };

const rotulo = (t) => {
  const partes = [t.nome];
  if (t.kcal_alvo) partes.push(`${t.kcal_alvo} kcal`);
  if (t.variante)  partes.push(t.variante);
  return partes.join(' | ');
};

const kcalDeMeals = (meals) => Math.round(
  Object.values(meals || {}).reduce((s, m) =>
    s + (m.alimentos || []).reduce((x, a) => x + (parseFloat(a.calorias) || 0), 0), 0)
);

/* ── Modal: Escolha um modelo (estilo WebDiet) ─────────────────────── */
export function EscolhaModeloModal({ onApply, onBlank, onClose }) {
  const [lista, setLista]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [sel, setSel]           = useState(null);       // id selecionado
  const [previewId, setPreviewId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [busy, setBusy]         = useState(false);

  const carregar = useCallback(async (termo) => {
    setLoading(true);
    try {
      const r = await dietTemplatesService.getAll(termo || '');
      if (r.success) setLista(r.data || []);
    } catch { /* silenciosa */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(''); }, [carregar]);
  useEffect(() => {
    const t = setTimeout(() => carregar(search), 300);
    return () => clearTimeout(t);
  }, [search, carregar]);

  const togglePreview = async (id) => {
    if (previewId === id) { setPreviewId(null); setPreviewData(null); return; }
    setPreviewId(id); setPreviewData(null);
    try {
      const r = await dietTemplatesService.get(id);
      if (r.success) setPreviewData(r.data.meals_plan || {});
    } catch { setPreviewData({}); }
  };

  const confirmar = async () => {
    if (!sel || busy) return;
    setBusy(true);
    try {
      const r = await dietTemplatesService.get(sel);
      if (r.success) onApply(r.data);
    } catch { /* mantém aberto */ }
    finally { setBusy(false); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={e => e.stopPropagation()}>
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#2f2a26' }}>Escolha um modelo</h3>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.83rem', color: '#736b64' }}>Você também pode iniciar um planejamento em branco</p>
        </div>

        {/* Busca */}
        <div style={{ position: 'relative', marginBottom: '0.85rem' }}>
          <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }}><Ico e="🔍" /></span>
          <input style={inputS} placeholder="Busque pelo nome ou condição..." value={search} onChange={e => setSearch(e.target.value)} autoFocus />
        </div>

        {/* Criar em branco */}
        <button onClick={onBlank}
          style={{ width: '100%', padding: '0.75rem', background: GREEN, color: '#ffffff', border: 'none', borderRadius: '10px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', marginBottom: '0.85rem' }}>
          Criar planejamento em branco (sem modelo)
        </button>

        {/* Lista */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#736b64' }}>Carregando modelos…</div>
        ) : lista.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#736b64' }}>Nenhum modelo encontrado.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '46vh', overflowY: 'auto' }}>
            {lista.map(t => {
              const ativo = sel === t.id;
              return (
                <div key={t.id}>
                  <div
                    onClick={() => setSel(t.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.65rem 0.8rem', borderRadius: '9px', cursor: 'pointer',
                      border: `1.5px solid ${ativo ? BRAND : '#f6f0ea'}`, background: ativo ? `${BRAND}12` : '#fbf8f5' }}>
                    <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#ffffff', background: t.is_own ? BRAND : '#a39b93', borderRadius: '5px', padding: '3px 5px', flexShrink: 0 }}>
                      {t.is_own ? 'MEU' : 'LN'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#2f2a26', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {emo(t.is_example ? '🔒 ' : '')}{rotulo(t)}
                      </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); togglePreview(t.id); }}
                      style={{ background: 'none', border: 'none', color: BRAND, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                      {previewId === t.id ? 'Ocultar' : 'Preview'}
                    </button>
                  </div>

                  {previewId === t.id && (
                    <div style={{ padding: '0.6rem 0.85rem', margin: '0.15rem 0 0.35rem', background: '#ffffff', border: '1px solid #ebe3da', borderRadius: '9px', fontSize: '0.8rem' }}>
                      {previewData === null ? (
                        <span style={{ color: '#736b64' }}>Carregando…</span>
                      ) : (
                        <>
                          <div style={{ fontWeight: 600, color: BRAND, marginBottom: '0.4rem' }}>≈ {kcalDeMeals(previewData)} kcal · {Object.keys(previewData).length} refeições</div>
                          {ordenarRefeicoes(previewData).filter(([, m]) => m.alimentos?.length).map(([k]) => (
                            <div key={k} style={{ marginBottom: '0.3rem' }}>
                              <span style={{ fontWeight: 600, color: '#4a4440' }}>{emo(MEAL_LABEL[k] || k)}: </span>
                              <span style={{ color: '#736b64' }}>{previewData[k].alimentos.map(a => a.alimento).join(', ')}</span>
                            </div>
                          ))}
                          {t.is_example && <div style={{ marginTop: '0.4rem', color: '#8a5f1a', fontSize: '0.76rem' }}><Ico e="⚠" sp />Modelo-exemplo — revise e ajuste antes de aplicar no paciente.</div>}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Confirmar */}
        <button onClick={confirmar} disabled={!sel || busy}
          style={{ width: '100%', marginTop: '1rem', padding: '0.8rem', background: sel ? GREEN : '#d9d0c6', color: '#ffffff', border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '0.95rem', cursor: sel ? 'pointer' : 'default' }}>
          {busy ? 'Aplicando…' : 'confirmar'}
        </button>
      </div>
    </div>
  );
}

/* ── Modal: Salvar plano atual como modelo ─────────────────────────── */
export function SalvarModeloModal({ meals, defaultName, onSaved, onClose }) {
  const [nome, setNome]       = useState(defaultName || '');
  const [categoria, setCat]   = useState('');
  const [kcal, setKcal]       = useState(kcalDeMeals(meals));
  const [variante, setVar]    = useState('Modelo 1');
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');

  const salvar = async () => {
    if (!nome.trim()) { setErr('Informe o nome do modelo.'); return; }
    setSaving(true); setErr('');
    try {
      const r = await dietTemplatesService.save({
        nome: nome.trim(), categoria: categoria.trim(),
        kcal_alvo: parseInt(kcal) || 0, variante: variante.trim(),
        meals_plan: meals,
      });
      if (r.success) onSaved(r.message || 'Modelo salvo!');
      else setErr(r.error || 'Falha ao salvar.');
    } catch { setErr('Erro ao salvar o modelo.'); }
    finally { setSaving(false); }
  };

  const label = { display: 'block', fontSize: '0.76rem', fontWeight: 600, color: '#736b64', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' };
  const field = { width: '100%', padding: '0.55rem 0.85rem', border: '1.5px solid #ebe3da', borderRadius: '9px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...box, maxWidth: '460px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#2f2a26' }}><Ico e="⭐" sp />Salvar como modelo</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#736b64', cursor: 'pointer', fontSize: '1rem' }}><Ico e="✕" /></button>
        </div>
        <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#736b64' }}>
          Salva as refeições atuais como um Cardápio Pronto reutilizável em qualquer paciente.
        </p>

        <div style={{ marginBottom: '0.85rem' }}>
          <label style={label}>Nome do modelo *</label>
          <input style={field} value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Cardápio para idosos" autoFocus />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.85rem' }}>
          <div>
            <label style={label}>Condição / categoria</label>
            <input style={field} value={categoria} onChange={e => setCat(e.target.value)} placeholder="Ex: Diabetes T2" />
          </div>
          <div>
            <label style={label}>Variante</label>
            <input style={field} value={variante} onChange={e => setVar(e.target.value)} placeholder="Modelo 1" />
          </div>
        </div>
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={label}>Kcal alvo</label>
          <input type="number" min="0" style={field} value={kcal} onChange={e => setKcal(e.target.value)} />
          <span style={{ fontSize: '0.74rem', color: '#736b64' }}>Calculado das refeições atuais — ajuste se quiser.</span>
        </div>

        {err && <div style={{ marginBottom: '0.75rem', fontSize: '0.82rem', color: '#c46a60' }}>{err}</div>}

        <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '0.6rem 1.2rem', background: '#ffffff', color: BRAND, border: `1.5px solid ${BRAND}`, borderRadius: '9px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}>Cancelar</button>
          <button onClick={salvar} disabled={saving || !nome.trim()}
            style={{ padding: '0.6rem 1.2rem', background: GREEN, color: '#ffffff', border: 'none', borderRadius: '9px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Salvando…' : 'Salvar modelo'}
          </button>
        </div>
      </div>
    </div>
  );
}
